import {useMemo, type FC} from 'react';

import {useAppSelector} from 'stores/redux_store';
import {ChipGroup, Chip, type ChipGroupProps} from '@time-webkit/all/organisms/chip-group';

import {getEmojiUrlByName} from 'utils/emoji_utils';
import {makeGetUniqueReactionsToPost} from 'utils/post_utils';
import {localizeMessage} from 'utils/utils';

import {EmojiWithTooltip} from './emoji_with_tooltip';
import styles from './styles.module.css';

interface ReactionTabsProps extends Pick<ChipGroupProps, 'onSelectionChange' | 'selectedKey'>{
    postId: string;
    isMobile: boolean;
}

const DESKTOP_MAX_CHIPS_VISIBLE = 6;
const MOBILE_MAX_CHIPS_VISIBLE = 3;
const EMPTY_REACTIONS_MAP = {};

export const ReactionTabs: FC<ReactionTabsProps> = ({postId, onSelectionChange, selectedKey, isMobile}) => {
    const getReactionsForPost = useMemo(() => makeGetUniqueReactionsToPost(), []);
    const reactionMapByUserId = useAppSelector((state) => {
        return getReactionsForPost(state, postId) || EMPTY_REACTIONS_MAP;
    });
    const chipsVisible = isMobile ? MOBILE_MAX_CHIPS_VISIBLE : DESKTOP_MAX_CHIPS_VISIBLE;

    const {uniqReactions, reactionCount} = useMemo(() => {
        const reactions = Object.values(reactionMapByUserId);
        const reactionCountsMap = reactions.reduce<Record<string, number>>((acc, reaction) => {
            acc[reaction.emoji_name] = acc[reaction.emoji_name] ? acc[reaction.emoji_name]! + 1 : 1;

            return acc;
        }, {});

        const uniqReactions = Object.entries(reactionCountsMap).map(([name, count]) => ({
            name,
            count,
        }));

        return {uniqReactions, reactionCount: reactions.length};
    }, [reactionMapByUserId]);

    return (
        <div className={styles.tabsContainer}>
            <ChipGroup
                moreLabel={localizeMessage('post_info.reactions.more', 'More')}
                maxChipsVisible={chipsVisible}
                onSelectionChange={onSelectionChange}
                selectedKey={selectedKey}
            >
                {[
                    <Chip
                        key='all'
                        title={
                            <div>
                                {`${localizeMessage('post_info.reactions.all-reactions', 'All')} `}
                                <Counter count={reactionCount} />
                            </div>
                        }
                    />,
                    ...uniqReactions.map((reaction, index) => {
                        const className = index + 1 >= chipsVisible ? styles.dropdown__title : styles.tabs__title;
                        return (
                            <Chip
                                key={reaction.name}
                                title={
                                    <ChipTitle
                                        count={reaction.count}
                                        className={className}
                                        name={reaction.name}
                                    />
                                }
                            />
                        );
                    }),
                ]}
            </ChipGroup>
        </div>
    );
};

const Counter = ({count}: {count: number}) => {
    return <span>{count > 999 ? '999+' : count}</span>;
};

const ChipTitle = ({count, className, name}: {count: number; name: string; className?: string}) => {
    const emojiUrl = useAppSelector((state) => getEmojiUrlByName(state, name));

    return (
        <div className={className}>
            <EmojiWithTooltip
                emojiName={name}
                emojiUrl={emojiUrl}
            />

            <Counter count={count} />
        </div>
    );
};
